import React from 'react';
import cx from 'classnames';

import { BackgroundView } from '../../background';
import { FlexFlowContext, resolveFlexLayout } from '../../flex/flexContext';
import { flexContainerStyles, getFlexInnerGridStyles, FLEX_MIN_HEIGHT } from '../../flex/flexStyles';
import { useContainerSelector } from '../../core/hooks';

import styles from './column.module.scss';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

const SectionColumnWidgetComponent = ({ className, style, widgetProps, breakpoint, children, columnGap, sectionStretched }) => {
  const id = get(widgetProps, 'uid');
  const type = get(widgetProps, 'type');

  const background = get(widgetProps, 'properties.desktop.background');
  const container = get(widgetProps, `properties.${breakpoint}.container`, {});
  const parentSection = useContainerSelector(id);
  const stretched = sectionStretched != null
    ? sectionStretched
    : get(parentSection, 'properties.desktop.stretched', false);
  const mobile = breakpoint === 'mobile';

  const flexMode = get(widgetProps, 'properties.desktop.flexBoxEnabled', false);
  const flexLayout = React.useMemo(
    () => resolveFlexLayout(widgetProps, breakpoint),
    [
      breakpoint,
      JSON.stringify(get(widgetProps, 'properties.desktop.flexProperty')),
      JSON.stringify(get(widgetProps, 'properties.mobile.flexProperty')),
    ]
  );
  const flexContextValue = React.useMemo(
    () => ({ flexMode, flexLayout }),
    [flexMode, flexLayout]
  );
  const childCount = React.Children.count(children);

  const flexInnerStyle = flexMode
    ? {
        ...flexContainerStyles(flexLayout, { childCount }),
        height: '100%',
        ...getFlexInnerGridStyles({
          stretched,
          minWidth: get(container, 'minWidth'),
          alignment: get(container, 'alignment', 'center'),
          mobile,
        }),
      }
    : undefined;

  var widgetStyle = {
    ...style,
    transform: 'none',
    ...getStyles(widgetProps, breakpoint, columnGap, flexMode, childCount)
  }

  if (flexMode) {
    widgetStyle = {
      ...widgetStyle,
      minHeight: flexContextValue.flexLayout?.minHeight || "0px",
    }
  }

  const content = flexMode ? (
    <FlexFlowContext.Provider value={flexContextValue}>
      <div className={styles.flexInner} style={flexInnerStyle}>
        {children}
      </div>
    </FlexFlowContext.Provider>
  ) : (
    <div className={styles.inner}>
      {children}
    </div>
  );

  return (
    <div id={id}
      className={cx(className, styles.column, 'cs_column', {
        [styles.flexMode]: flexMode,
      })}
      style={widgetStyle}
      data-widget-id={id}
      data-widget-type={type}
    >
      <BackgroundView background={background} breakpoint={breakpoint} />
      {content}
    </div>
  );
};

function getStyles(widget, breakpoint, columnGap, flexMode, childCount) {
  var columnStyles = {};
  const emptyFlexColumn = flexMode && childCount === 0;

  if (breakpoint === 'desktop') {
    const width = parseFloat(get(widget, `properties.${breakpoint}.layout.width`, 100));

    columnStyles = {
      ...columnStyles,
      maxWidth: `calc(${width}% - ${columnGap}px)`,
      ...(flexMode
        ? {
            height: 'auto',
            ...(emptyFlexColumn && { minHeight: `${FLEX_MIN_HEIGHT}px` }),
          }
        : { height: '100%' }),
    };
  }

  if (breakpoint === 'mobile' && flexMode) {
    columnStyles = {
      ...columnStyles,
      height: 'auto',
      ...(emptyFlexColumn && { minHeight: `${FLEX_MIN_HEIGHT}px` }),
    };
  }

  return columnStyles;
}


const sectionColumnWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'breakpoint', 'children', 'columnGap', 'sectionStretched'];
  const next = pick(nextProps, paths);
  const previous = pick(prevProps, paths);

  const equals = isEqual(next, previous);
  return equals;
}

export const SectionColumnWidget = React.memo(SectionColumnWidgetComponent, sectionColumnWidgetEquals);
